import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  ChevronLeft, BookOpen, Clock, CheckCircle2, Lightbulb,
  Play, Users, DollarSign, Settings, BarChart3, FileText,
  Camera, Sparkles, Code, Award, Calendar, MessageSquare,
  CreditCard, Shield, UserPlus, Search, Bell, Mail
} from 'lucide-react';

// Help article content database
const helpArticles = {
  'quick-start': {
    title: 'Quick Start Video Tutorial',
    category: 'Getting Started',
    duration: '10 min',
    icon: Play,
    description: 'Get up and running with MyMedMember in minutes',
    content: [
      {
        heading: 'Welcome to MyMedMember',
        text: 'This quick start guide will walk you through the essential steps to set up your clinic and start managing memberships effectively.'
      },
      {
        heading: 'Step 1: Complete Your Profile',
        text: 'Navigate to Settings > Practice Profile to add your clinic name, logo, contact information, and business hours. This information will appear on member cards and communications.'
      },
      {
        heading: 'Step 2: Create Membership Plans',
        text: 'Go to Membership Plans to create your first plan. Define the name, price, duration, and included services. You can use our AI suggestions for optimal pricing.'
      },
      {
        heading: 'Step 3: Add Your First Member',
        text: 'Navigate to Members and click "Add Member". Enter patient details and optionally assign a membership plan during registration.'
      },
      {
        heading: 'Step 4: Set Up Payments',
        text: 'Configure your payment gateway in Settings > Billing. Connect Stripe to accept online payments and process membership fees.'
      }
    ],
    tips: [
      'Use the AI membership suggestions to optimize your pricing strategy',
      'Enable email notifications to keep members informed',
      'Set up your branding for a professional member experience'
    ]
  },
  'first-steps': {
    title: 'First Steps Checklist',
    category: 'Getting Started',
    duration: '5 min',
    icon: CheckCircle2,
    description: 'Essential tasks to complete when setting up your clinic',
    content: [
      {
        heading: 'Account Setup Checklist',
        text: 'Complete these essential steps to get your clinic ready:'
      }
    ],
    checklist: [
      'Complete your clinic profile with name, logo, and contact info',
      'Add your business hours and location',
      'Upload your clinic logo for branding',
      'Configure email notification settings',
      'Set up your payment gateway (Stripe)',
      'Create at least one membership plan',
      'Invite team members (if applicable)',
      'Test the member registration flow',
      'Review and customize email templates',
      'Set up your public-facing widget (optional)'
    ],
    tips: [
      'Take your time with the profile setup - it affects your branding',
      'Start with 2-3 membership tiers before adding more'
    ]
  },
  'profile-setup': {
    title: 'Setting Up Your Profile',
    category: 'Getting Started',
    duration: '8 min',
    icon: Settings,
    description: 'Configure your clinic profile and branding',
    content: [
      {
        heading: 'Accessing Profile Settings',
        text: 'Navigate to Settings > Practice Profile from your admin dashboard. Here you can customize all aspects of your clinic\'s identity.'
      },
      {
        heading: 'Basic Information',
        text: 'Enter your clinic name, phone number, email, and physical address. This information appears on member cards, receipts, and communications.'
      },
      {
        heading: 'Branding',
        text: 'Upload your clinic logo (recommended: 200x200px PNG with transparent background). Choose your primary brand color which will be used throughout the platform.'
      },
      {
        heading: 'Business Hours',
        text: 'Set your operating hours for each day of the week. These are displayed to members and used for appointment scheduling.'
      }
    ],
    tips: [
      'Use a high-quality logo for the best appearance on member cards',
      'Keep your contact information up to date for member communications'
    ]
  },
  'revenue-projections': {
    title: 'Revenue Projections Guide',
    category: 'AI Features',
    duration: '7 min',
    icon: BarChart3,
    description: 'Understand and use AI-powered revenue forecasting',
    content: [
      {
        heading: 'What are Revenue Projections?',
        text: 'Our AI analyzes your membership data, growth trends, and industry benchmarks to forecast your expected revenue over the coming months.'
      },
      {
        heading: 'Accessing Projections',
        text: 'Navigate to Dashboard > Analytics to view your revenue projections. The graph shows historical data and future predictions.'
      },
      {
        heading: 'Understanding the Data',
        text: 'The projection considers: current member count, average plan value, historical churn rate, seasonal trends, and growth trajectory.'
      },
      {
        heading: 'Using Projections for Planning',
        text: 'Use these insights to plan staffing, marketing campaigns, and resource allocation. Adjust your strategies based on predicted growth or decline.'
      }
    ],
    tips: [
      'Review projections monthly to track accuracy',
      'Factor in planned marketing campaigns for better estimates',
      'Use break-even analysis alongside projections for financial planning'
    ]
  },
  'business-intelligence': {
    title: 'Business Intelligence Metrics',
    category: 'AI Features',
    duration: '10 min',
    icon: Sparkles,
    description: 'Key metrics and insights for your clinic',
    content: [
      {
        heading: 'Key Performance Indicators (KPIs)',
        text: 'Track essential metrics including: Member Retention Rate, Average Revenue Per Member (ARPM), Churn Rate, and Lifetime Value (LTV).'
      },
      {
        heading: 'Member Analytics',
        text: 'Understand your member base with demographics, plan distribution, engagement levels, and renewal patterns.'
      },
      {
        heading: 'Financial Metrics',
        text: 'Monitor Monthly Recurring Revenue (MRR), payment success rates, outstanding balances, and revenue by plan type.'
      },
      {
        heading: 'Growth Indicators',
        text: 'Track new member acquisition, referral sources, conversion rates from trials, and membership upgrades/downgrades.'
      }
    ],
    tips: [
      'Set up weekly reports to stay on top of key metrics',
      'Compare month-over-month to identify trends',
      'Use insights to optimize your membership offerings'
    ]
  },
  'break-even': {
    title: 'Understanding Break-Even Analysis',
    category: 'AI Features',
    duration: '6 min',
    icon: DollarSign,
    description: 'Calculate when your membership program becomes profitable',
    content: [
      {
        heading: 'What is Break-Even Analysis?',
        text: 'Break-even analysis helps you determine how many members you need at various price points to cover your costs and start generating profit.'
      },
      {
        heading: 'Viewing Your Break-Even Point',
        text: 'Access the break-even calculator in Dashboard > Analytics > Break-Even. Enter your fixed costs (rent, salaries, software) and variable costs per member.'
      },
      {
        heading: 'Interpreting Results',
        text: 'The analysis shows you the exact number of members needed at your current pricing to cover costs. It also shows scenarios at different price points.'
      },
      {
        heading: 'Strategic Planning',
        text: 'Use break-even data to set realistic growth targets, evaluate pricing changes, and make informed decisions about expanding services.'
      }
    ],
    tips: [
      'Update your cost inputs quarterly for accurate analysis',
      'Consider both direct and indirect costs',
      'Use "what-if" scenarios to plan pricing changes'
    ]
  },
  'templates': {
    title: 'Using Pre-Built Templates',
    category: 'Membership Templates',
    duration: '5 min',
    icon: FileText,
    description: 'Jumpstart your membership plans with proven templates',
    content: [
      {
        heading: 'Accessing Templates',
        text: 'Navigate to Membership Plans > Templates to browse our library of pre-built membership plan templates designed for various clinic types.'
      },
      {
        heading: 'Template Categories',
        text: 'Templates are organized by specialty: Primary Care, Dental, Aesthetic Medicine, Mental Health, Physical Therapy, and more.'
      },
      {
        heading: 'Using a Template',
        text: 'Click on any template to preview its structure. Select "Use Template" to create a new plan based on it. You can then customize pricing, services, and terms.'
      },
      {
        heading: 'Customization',
        text: 'All templates are fully customizable. Modify the name, pricing, duration, included services, and any other details to match your clinic\'s needs.'
      }
    ],
    tips: [
      'Start with a template close to your needs, then customize',
      'Review successful templates in your specialty for inspiration',
      'Use AI suggestions to optimize template pricing for your market'
    ]
  },
  'custom-templates': {
    title: 'Creating Custom Templates',
    category: 'Membership Templates',
    duration: '8 min',
    icon: FileText,
    description: 'Build your own reusable membership templates',
    content: [
      {
        heading: 'Why Create Custom Templates?',
        text: 'Custom templates save time when creating similar plans and ensure consistency across your membership offerings.'
      },
      {
        heading: 'Creating a Template',
        text: 'Go to Membership Plans > Templates > Create New. Define the structure, default pricing, services, and terms.'
      },
      {
        heading: 'Template Components',
        text: 'Include: Plan name pattern, pricing tiers, duration options, service list, terms and conditions, and enrollment requirements.'
      },
      {
        heading: 'Managing Templates',
        text: 'Edit, duplicate, or archive templates from the Templates management page. Share templates across multiple clinic locations if applicable.'
      }
    ],
    tips: [
      'Create templates for your most common plan types',
      'Include detailed service descriptions for clarity',
      'Review and update templates periodically'
    ]
  },
  'template-categories': {
    title: 'Template Categories Explained',
    category: 'Membership Templates',
    duration: '4 min',
    icon: FileText,
    description: 'Understanding the different template categories',
    content: [
      {
        heading: 'Primary Care',
        text: 'Templates designed for family medicine, internal medicine, and general practice clinics. Focus on preventive care and routine visits.'
      },
      {
        heading: 'Specialty Care',
        text: 'Templates for specialized practices including cardiology, dermatology, orthopedics, and other medical specialties.'
      },
      {
        heading: 'Aesthetic Medicine',
        text: 'Templates optimized for med spas, plastic surgery practices, and aesthetic clinics. Include treatment packages and maintenance plans.'
      },
      {
        heading: 'Dental',
        text: 'Dental membership templates covering preventive care, discount plans, and comprehensive treatment packages.'
      },
      {
        heading: 'Mental Health',
        text: 'Templates for therapy practices, counseling centers, and psychiatric clinics with session-based structures.'
      }
    ],
    tips: [
      'Choose a category that matches your primary services',
      'Mix templates from different categories if you offer diverse services'
    ]
  },
  'create-plans': {
    title: 'Creating Membership Plans',
    category: 'Membership Plans',
    duration: '10 min',
    icon: Award,
    description: 'Step-by-step guide to creating effective membership plans',
    content: [
      {
        heading: 'Getting Started',
        text: 'Navigate to Membership Plans from your dashboard and click "Create New Plan" to begin.'
      },
      {
        heading: 'Basic Information',
        text: 'Enter the plan name, description, and category. Choose a name that clearly communicates the plan\'s value proposition.'
      },
      {
        heading: 'Pricing & Duration',
        text: 'Set the membership price and duration (monthly, quarterly, annual). Consider offering discounts for longer commitments.'
      },
      {
        heading: 'Included Services',
        text: 'Define what services are included in the membership. Be specific about quantities, frequencies, and any limitations.'
      },
      {
        heading: 'Enrollment Fee',
        text: 'Optionally add a one-time enrollment fee for new members. This can cover administrative costs or initial assessments.'
      }
    ],
    tips: [
      'Use clear, benefit-focused plan names',
      'Offer 3-4 tiers to give members choice without overwhelm',
      'Highlight the savings compared to paying per-service'
    ]
  },
  'manage-plans': {
    title: 'Editing & Deleting Plans',
    category: 'Membership Plans',
    duration: '5 min',
    icon: Settings,
    description: 'How to modify or remove existing membership plans',
    content: [
      {
        heading: 'Editing a Plan',
        text: 'Click on any plan from your Membership Plans list to view details. Click "Edit" to modify the plan\'s name, pricing, services, or other attributes.'
      },
      {
        heading: 'Price Changes',
        text: 'When changing prices, you can choose to apply changes only to new members or also update existing member rates (with notification).'
      },
      {
        heading: 'Deactivating Plans',
        text: 'To stop selling a plan without affecting current members, toggle the plan to "Inactive". Existing members can continue until their term ends.'
      },
      {
        heading: 'Deleting Plans',
        text: 'Plans with no active members can be permanently deleted. Plans with active members must first be deactivated and members transitioned.'
      }
    ],
    tips: [
      'Always communicate price changes to affected members in advance',
      'Consider grandfathering existing members at old rates',
      'Archive rather than delete plans for historical records'
    ]
  },
  'pricing-strategies': {
    title: 'Plan Pricing Strategies',
    category: 'Membership Plans',
    duration: '12 min',
    icon: DollarSign,
    description: 'Optimize your membership pricing for growth and profitability',
    content: [
      {
        heading: 'Understanding Value-Based Pricing',
        text: 'Price your memberships based on the value delivered to members, not just your costs. Consider the savings and convenience members receive.'
      },
      {
        heading: 'Tiered Pricing',
        text: 'Offer multiple tiers (Basic, Premium, VIP) to capture different market segments. Each tier should have clear value differentiation.'
      },
      {
        heading: 'Psychological Pricing',
        text: 'Use pricing psychology: $99 vs $100, annual billing discounts, and anchoring high-value tiers to make mid-tier attractive.'
      },
      {
        heading: 'Competitive Analysis',
        text: 'Research competitor pricing in your area. Position your plans competitively while emphasizing unique value propositions.'
      }
    ],
    tips: [
      'Test different price points with small member groups',
      'Monitor conversion rates at each pricing tier',
      'Review and adjust pricing annually based on costs and market'
    ]
  },
  'activate-plans': {
    title: 'Activating Plans',
    category: 'Membership Plans',
    duration: '3 min',
    icon: CheckCircle2,
    description: 'Make your membership plans available to patients',
    content: [
      {
        heading: 'Plan Status',
        text: 'Each plan has a status: Draft, Active, or Inactive. Only Active plans are available for member enrollment.'
      },
      {
        heading: 'Activating a Plan',
        text: 'From the plan details page, toggle the status to "Active". The plan immediately becomes available in the enrollment process.'
      },
      {
        heading: 'Pre-Launch Checklist',
        text: 'Before activating: verify pricing, review included services, test the enrollment flow, and ensure payment processing is configured.'
      }
    ],
    tips: [
      'Activate one plan at a time to monitor performance',
      'Have staff test the full enrollment process before launch',
      'Prepare marketing materials before activation'
    ]
  },
  'gallery': {
    title: 'Before & After Gallery',
    category: 'Aesthetic Features',
    duration: '8 min',
    icon: Camera,
    description: 'Showcase your results with a professional gallery',
    content: [
      {
        heading: 'Setting Up Your Gallery',
        text: 'Navigate to Aesthetic > Gallery to manage your before and after photos. Create categories for different treatment types.'
      },
      {
        heading: 'Adding Photos',
        text: 'Upload matched before/after pairs with consistent lighting, angles, and backgrounds. Add treatment details and timeframes.'
      },
      {
        heading: 'Privacy & Consent',
        text: 'Always obtain written consent before publishing patient photos. Our system includes digital consent tracking and watermarking.'
      },
      {
        heading: 'Public Display',
        text: 'Choose which photos to display publicly on your website widget versus keeping private for in-office consultations only.'
      }
    ],
    tips: [
      'Use consistent photography standards for all photos',
      'Include treatment details and timeframes for context',
      'Regularly update with recent results'
    ]
  },
  'packages': {
    title: 'Treatment Packages',
    category: 'Aesthetic Features',
    duration: '7 min',
    icon: Award,
    description: 'Create and manage treatment packages',
    content: [
      {
        heading: 'Creating Packages',
        text: 'Navigate to Aesthetic > Packages to create bundled treatment offerings. Combine multiple services at a discounted package rate.'
      },
      {
        heading: 'Package Components',
        text: 'Define included treatments, quantities, validity period, and total package price. Show savings compared to individual pricing.'
      },
      {
        heading: 'Package Tracking',
        text: 'Track package usage per patient. The system automatically deducts treatments as they\'re used and alerts when packages near completion.'
      }
    ],
    tips: [
      'Create packages that encourage treatment completion',
      'Offer package financing options for higher-value bundles',
      'Use packages to introduce patients to complementary services'
    ]
  },
  'consultations': {
    title: 'Consultation Bookings',
    category: 'Aesthetic Features',
    duration: '6 min',
    icon: Calendar,
    description: 'Manage aesthetic consultation appointments',
    content: [
      {
        heading: 'Booking System',
        text: 'Patients can book consultations through your public page or the member portal. Set consultation types, durations, and availability.'
      },
      {
        heading: 'Consultation Types',
        text: 'Create different consultation types: Initial Assessment, Treatment Planning, Follow-up, Virtual Consultation, etc.'
      },
      {
        heading: 'Consultation Notes',
        text: 'Document consultation findings, recommendations, and treatment plans. Attach photos and consent forms to the record.'
      }
    ],
    tips: [
      'Offer complimentary consultations to increase conversions',
      'Send consultation confirmation and preparation instructions',
      'Follow up promptly after consultations'
    ]
  },
  'photo-management': {
    title: 'Photo Management Best Practices',
    category: 'Aesthetic Features',
    duration: '5 min',
    icon: Camera,
    description: 'Guidelines for clinical photography',
    content: [
      {
        heading: 'Standardization',
        text: 'Use consistent camera settings, lighting, background, and patient positioning for all clinical photos. This ensures accurate comparisons.'
      },
      {
        heading: 'Equipment',
        text: 'A smartphone with good camera works well. Consider a dedicated photo station with proper lighting for best results.'
      },
      {
        heading: 'Organization',
        text: 'Photos are automatically organized by patient, date, and treatment type. Add tags for easy searching and filtering.'
      },
      {
        heading: 'Backup & Security',
        text: 'All photos are encrypted and backed up automatically. Access is restricted to authorized staff only.'
      }
    ],
    tips: [
      'Take photos at consistent intervals (e.g., every 4 weeks)',
      'Include measurement references when relevant',
      'Document lighting and camera settings for consistency'
    ]
  },
  'add-members': {
    title: 'Adding New Members',
    category: 'Patient Management',
    duration: '5 min',
    icon: UserPlus,
    description: 'Register new patients and members',
    content: [
      {
        heading: 'Registration Methods',
        text: 'Add members through: Staff dashboard (walk-in), Patient self-registration, Website widget, or Bulk import.'
      },
      {
        heading: 'Required Information',
        text: 'Collect: Full name, email address (for login), phone number, date of birth, and emergency contact information.'
      },
      {
        heading: 'Optional Plan Assignment',
        text: 'Assign a membership plan during registration or add later. Process payment if assigning a paid plan.'
      },
      {
        heading: 'Welcome Email',
        text: 'New members receive an automated welcome email with login credentials and getting started information.'
      }
    ],
    tips: [
      'Verify email addresses to ensure members can log in',
      'Use the walk-in registration guide for front desk staff',
      'Consider a simplified registration flow for walk-ins'
    ]
  },
  'appointments': {
    title: 'Appointment Scheduling',
    category: 'Patient Management',
    duration: '8 min',
    icon: Calendar,
    description: 'Manage patient appointments effectively',
    content: [
      {
        heading: 'Calendar Overview',
        text: 'Access the appointment calendar from Dashboard > Appointments. View by day, week, or month. Filter by provider or service type.'
      },
      {
        heading: 'Booking Appointments',
        text: 'Click any available slot to create an appointment. Select the patient, service type, provider, and duration.'
      },
      {
        heading: 'Patient Self-Booking',
        text: 'Members can book appointments through the patient portal based on your availability settings.'
      },
      {
        heading: 'Reminders',
        text: 'Automated reminders are sent via email and SMS (if configured) before appointments. Customize timing and content.'
      }
    ],
    tips: [
      'Block time for walk-ins during busy periods',
      'Set buffer time between appointments',
      'Review and confirm next-day appointments daily'
    ]
  },
  'messaging': {
    title: 'Messaging Patients',
    category: 'Patient Management',
    duration: '6 min',
    icon: MessageSquare,
    description: 'Communicate effectively with your members',
    content: [
      {
        heading: 'In-App Messaging',
        text: 'Send secure messages to patients through the platform. Messages appear in both your dashboard and the patient portal.'
      },
      {
        heading: 'Bulk Communications',
        text: 'Send announcements to all members or filtered groups. Use for: policy updates, promotions, or health reminders.'
      },
      {
        heading: 'Email Templates',
        text: 'Use and customize email templates for common communications: appointment reminders, renewal notices, and newsletters.'
      },
      {
        heading: 'Message History',
        text: 'All communications are logged and searchable. View conversation history for any patient.'
      }
    ],
    tips: [
      'Respond to patient messages within 24 hours',
      'Use templates for consistent, professional communication',
      'Segment messages for relevance'
    ]
  },
  'member-dashboard': {
    title: 'Member Dashboard Overview',
    category: 'Patient Management',
    duration: '5 min',
    icon: Users,
    description: 'Understanding the patient portal experience',
    content: [
      {
        heading: 'Patient Portal Access',
        text: 'Members access their portal at your clinic\'s patient URL or through the main login page. They use their email and password to log in.'
      },
      {
        heading: 'Dashboard Features',
        text: 'Members can view: membership status, upcoming appointments, message history, payment history, and member card.'
      },
      {
        heading: 'Self-Service Options',
        text: 'Members can: book appointments, message the clinic, update profile information, and view/download their member card.'
      },
      {
        heading: 'Mobile Access',
        text: 'The patient portal is fully responsive and works on all devices. Members can add their member card to mobile wallets.'
      }
    ],
    tips: [
      'Encourage members to use the portal for self-service',
      'Promote the mobile member card feature',
      'Keep portal content up to date'
    ]
  },
  'payments': {
    title: 'Payment Processing',
    category: 'Billing & Payments',
    duration: '8 min',
    icon: CreditCard,
    description: 'Accept and manage membership payments',
    content: [
      {
        heading: 'Payment Methods',
        text: 'Accept: Credit/Debit cards (via Stripe), Cash, Check, Bank Transfer, and HSA/FSA cards where applicable.'
      },
      {
        heading: 'Online Payments',
        text: 'Stripe integration enables secure online payments. Members can pay during enrollment or set up recurring billing.'
      },
      {
        heading: 'Manual Payments',
        text: 'Record cash, check, and other manual payments through the staff dashboard. Generate receipts for all transactions.'
      },
      {
        heading: 'Payment History',
        text: 'View complete payment history for each member. Export reports for accounting and reconciliation.'
      }
    ],
    tips: [
      'Enable multiple payment methods for member convenience',
      'Set up automatic recurring billing when possible',
      'Reconcile payments weekly'
    ]
  },
  'service-fees': {
    title: 'Service Fees Setup',
    category: 'Billing & Payments',
    duration: '5 min',
    icon: DollarSign,
    description: 'Configure fees for services and transactions',
    content: [
      {
        heading: 'Enrollment Fees',
        text: 'Set one-time fees for new member enrollment. These can cover administrative costs, initial assessments, or welcome kits.'
      },
      {
        heading: 'Service Fees',
        text: 'Define pricing for services that are not included in membership plans. Create a service menu with standard pricing.'
      },
      {
        heading: 'Late Fees',
        text: 'Configure optional late fees for overdue membership payments. Set grace periods and fee amounts.'
      }
    ],
    tips: [
      'Be transparent about all fees in membership terms',
      'Consider waiving enrollment fees for promotions',
      'Review fee structure annually'
    ]
  },
  'invoicing': {
    title: 'Invoicing & Receipts',
    category: 'Billing & Payments',
    duration: '6 min',
    icon: FileText,
    description: 'Generate and manage financial documents',
    content: [
      {
        heading: 'Automatic Receipts',
        text: 'Receipts are automatically generated and emailed for all payments. Members can also download receipts from their portal.'
      },
      {
        heading: 'Invoice Generation',
        text: 'Create invoices for outstanding balances or custom charges. Send invoices via email with payment links.'
      },
      {
        heading: 'Customization',
        text: 'Customize invoice and receipt templates with your branding, terms, and tax information.'
      },
      {
        heading: 'Export',
        text: 'Export invoice and receipt data for accounting software integration. Available in CSV and PDF formats.'
      }
    ],
    tips: [
      'Include clear payment terms on invoices',
      'Set up automatic invoice reminders',
      'Keep digital copies of all receipts'
    ]
  },
  'stripe': {
    title: 'Stripe Connect Integration',
    category: 'Billing & Payments',
    duration: '10 min',
    icon: CreditCard,
    description: 'Set up Stripe for payment processing',
    content: [
      {
        heading: 'Connecting Stripe',
        text: 'Navigate to Settings > Billing > Payment Gateway. Click "Connect Stripe" to link your Stripe account or create a new one.'
      },
      {
        heading: 'Configuration',
        text: 'Once connected, configure your payment settings: accepted card types, currency, and automatic payment retries.'
      },
      {
        heading: 'Testing',
        text: 'Use test mode to verify your setup before going live. Process test transactions to ensure everything works correctly.'
      },
      {
        heading: 'Going Live',
        text: 'Switch from test to live mode when ready. Verify your bank account connection for payouts.'
      }
    ],
    tips: [
      'Complete Stripe account verification promptly',
      'Test the full payment flow before launching',
      'Monitor your Stripe dashboard for payment issues'
    ]
  },
  'analytics': {
    title: 'Dashboard Analytics',
    category: 'Reports & Analytics',
    duration: '7 min',
    icon: BarChart3,
    description: 'Understanding your dashboard metrics',
    content: [
      {
        heading: 'Overview Dashboard',
        text: 'The main dashboard shows key metrics at a glance: total members, active memberships, revenue, and recent activity.'
      },
      {
        heading: 'Trend Analysis',
        text: 'View trends over time with interactive charts. Compare periods to identify growth patterns or areas of concern.'
      },
      {
        heading: 'Real-Time Data',
        text: 'Dashboard data updates in real-time. See immediate impact of new enrollments, payments, and cancellations.'
      }
    ],
    tips: [
      'Check your dashboard daily for quick health checks',
      'Set up alerts for significant changes',
      'Use dashboard data in team meetings'
    ]
  },
  'scheduled-reports': {
    title: 'Scheduled Reports',
    category: 'Reports & Analytics',
    duration: '5 min',
    icon: FileText,
    description: 'Automate your reporting workflow',
    content: [
      {
        heading: 'Report Types',
        text: 'Schedule regular reports: Membership Summary, Revenue Report, Churn Analysis, and Custom Reports.'
      },
      {
        heading: 'Setting Up Schedules',
        text: 'Navigate to Reports > Scheduled Reports. Choose report type, frequency (daily, weekly, monthly), and recipients.'
      },
      {
        heading: 'Delivery Options',
        text: 'Reports can be emailed automatically or made available for download in the reports section.'
      }
    ],
    tips: [
      'Schedule weekly summaries for stakeholders',
      'Set monthly reports to run on the 1st',
      'Include key team members in report distribution'
    ]
  },
  'revenue-tracking': {
    title: 'Revenue Tracking',
    category: 'Reports & Analytics',
    duration: '6 min',
    icon: DollarSign,
    description: 'Monitor and analyze your revenue',
    content: [
      {
        heading: 'Revenue Dashboard',
        text: 'View your revenue dashboard in Analytics > Revenue. See total revenue, MRR, and revenue by plan type.'
      },
      {
        heading: 'Revenue Breakdown',
        text: 'Analyze revenue by: membership plan, payment method, time period, and new vs renewal payments.'
      },
      {
        heading: 'Forecasting',
        text: 'Use historical data and current trends to forecast future revenue. Adjust projections based on planned activities.'
      }
    ],
    tips: [
      'Track MRR as your primary revenue metric',
      'Monitor revenue per member trends',
      'Compare actual vs projected revenue monthly'
    ]
  },
  'growth-metrics': {
    title: 'Member Growth Metrics',
    category: 'Reports & Analytics',
    duration: '5 min',
    icon: Users,
    description: 'Track and optimize member acquisition',
    content: [
      {
        heading: 'Growth Dashboard',
        text: 'View growth metrics in Analytics > Growth. Track new members, churn, net growth, and growth rate.'
      },
      {
        heading: 'Acquisition Sources',
        text: 'Understand where your members come from: website, referrals, walk-ins, or marketing campaigns.'
      },
      {
        heading: 'Retention Analysis',
        text: 'Monitor retention rates by cohort. Identify which member groups have the highest lifetime value.'
      }
    ],
    tips: [
      'Set monthly growth targets',
      'Invest in high-performing acquisition channels',
      'Focus on retention as much as acquisition'
    ]
  },
  'practice-setup': {
    title: 'Practice Profile Setup',
    category: 'Settings',
    duration: '8 min',
    icon: Settings,
    description: 'Configure your practice information',
    content: [
      {
        heading: 'Basic Information',
        text: 'Enter your practice name, address, phone, email, and website. This appears on all member-facing materials.'
      },
      {
        heading: 'Branding',
        text: 'Upload your logo, choose primary brand colors, and customize the look of your member portal and communications.'
      },
      {
        heading: 'Business Hours',
        text: 'Set your operating hours for each day. Include lunch breaks or other closed periods as needed.'
      },
      {
        heading: 'Location & Map',
        text: 'Add your precise location for the integrated map. Members will see directions to your clinic.'
      }
    ],
    tips: [
      'Keep contact information current',
      'Use a high-quality logo (300x300px recommended)',
      'Review your profile quarterly for accuracy'
    ]
  },
  'user-management': {
    title: 'User Management',
    category: 'Settings',
    duration: '7 min',
    icon: Users,
    description: 'Manage staff accounts and permissions',
    content: [
      {
        heading: 'Adding Staff Users',
        text: 'Navigate to Settings > Team to invite new staff members. Enter their email and assign a role.'
      },
      {
        heading: 'Role Types',
        text: 'Available roles: Admin (full access), Manager, Staff, Receptionist, Nurse, Provider. Each has appropriate permissions.'
      },
      {
        heading: 'Permissions',
        text: 'Each role has predefined permissions. Admins can customize permissions for specific needs.'
      },
      {
        heading: 'Deactivating Users',
        text: 'Deactivate users who leave your practice. Their access is immediately revoked while records are preserved.'
      }
    ],
    tips: [
      'Use the principle of least privilege',
      'Review user access quarterly',
      'Deactivate former employees promptly'
    ]
  },
  'subscription': {
    title: 'Subscription Plans',
    category: 'Settings',
    duration: '5 min',
    icon: CreditCard,
    description: 'Manage your MyMedMember subscription',
    content: [
      {
        heading: 'Current Plan',
        text: 'View your current subscription plan, features, and limits in Settings > Subscription.'
      },
      {
        heading: 'Upgrading',
        text: 'Upgrade to access more features, higher member limits, and advanced capabilities.'
      },
      {
        heading: 'Billing History',
        text: 'View past invoices and payment history for your subscription.'
      }
    ],
    tips: [
      'Review your plan limits regularly',
      'Upgrade before hitting member limits',
      'Contact support for custom plans'
    ]
  },
  'security': {
    title: 'Security & Privacy',
    category: 'Settings',
    duration: '6 min',
    icon: Shield,
    description: 'Configure security settings and understand privacy features',
    content: [
      {
        heading: 'Password Requirements',
        text: 'All accounts require strong passwords: minimum 8 characters with mixed case, numbers, and symbols.'
      },
      {
        heading: 'Session Security',
        text: 'Configure automatic session timeout for staff accounts. Set appropriate durations based on your security needs.'
      },
      {
        heading: 'Data Privacy',
        text: 'Patient data is encrypted at rest and in transit. Access is logged and auditable.'
      },
      {
        heading: 'HIPAA Compliance',
        text: 'Our platform is designed to support HIPAA compliance. Review our HIPAA documentation for details.'
      }
    ],
    tips: [
      'Enable two-factor authentication when available',
      'Train staff on security best practices',
      'Review access logs regularly'
    ]
  },
  'quick-start-ai': {
    title: 'Getting Started with AI Suggestions',
    category: 'AI Features',
    duration: '5 min',
    icon: Sparkles,
    description: 'Learn how to use AI-powered membership suggestions',
    content: [
      {
        heading: 'What are AI Suggestions?',
        text: 'Our AI analyzes your clinic type, location, and industry data to recommend optimal membership plans and pricing strategies tailored to your practice.'
      },
      {
        heading: 'Accessing AI Suggestions',
        text: 'Navigate to Membership Plans and click "Get AI Suggestions". The AI will analyze your profile and generate customized recommendations.'
      },
      {
        heading: 'Understanding Recommendations',
        text: 'Each suggestion includes: recommended pricing, plan structure, projected enrollment, and revenue estimates based on similar clinics.'
      },
      {
        heading: 'Applying Suggestions',
        text: 'Review the AI recommendations and click "Apply" to create a new plan based on the suggestion. You can further customize before saving.'
      }
    ],
    tips: [
      'Complete your clinic profile for more accurate suggestions',
      'Review multiple AI suggestions before deciding',
      'Use AI suggestions as a starting point, then customize'
    ]
  },
  'quick-start-plan': {
    title: 'Create Your First Membership Plan',
    category: 'Getting Started',
    duration: '3 min',
    icon: Award,
    description: 'Quick guide to creating your first membership plan',
    content: [
      {
        heading: 'Step 1: Navigate to Plans',
        text: 'From your dashboard, click on "Membership Plans" in the sidebar navigation.'
      },
      {
        heading: 'Step 2: Create New Plan',
        text: 'Click the "Create New Plan" button. You can also use "Get AI Suggestions" for recommendations.'
      },
      {
        heading: 'Step 3: Fill in Details',
        text: 'Enter plan name, price, duration (monthly/annual), and a description of what\'s included.'
      },
      {
        heading: 'Step 4: Activate',
        text: 'Toggle the plan to "Active" status to make it available for member enrollment.'
      }
    ],
    tips: [
      'Start with a simple plan structure',
      'Use clear, benefit-focused naming',
      'Consider offering multiple tiers'
    ]
  },
  'quick-start-setup': {
    title: 'Set Up Your Practice Profile',
    category: 'Getting Started',
    duration: '8 min',
    icon: Settings,
    description: 'Complete your clinic profile setup',
    content: [
      {
        heading: 'Step 1: Access Settings',
        text: 'Navigate to Settings > Practice Profile from your admin dashboard.'
      },
      {
        heading: 'Step 2: Basic Information',
        text: 'Enter your clinic name, address, phone number, and email. This appears on all member communications.'
      },
      {
        heading: 'Step 3: Upload Logo',
        text: 'Add your clinic logo (recommended: 200x200px PNG). This appears on member cards and the patient portal.'
      },
      {
        heading: 'Step 4: Business Hours',
        text: 'Set your operating hours for each day of the week. These are displayed to members.'
      },
      {
        heading: 'Step 5: Branding Colors',
        text: 'Choose your primary brand color to customize the look of your member portal and communications.'
      },
      {
        heading: 'Step 6: Save Changes',
        text: 'Click "Save" to apply your profile changes. Preview how it looks in the member portal.'
      }
    ],
    tips: [
      'Use a high-quality logo for professional appearance',
      'Keep contact information accurate',
      'Review your profile from a member\'s perspective'
    ]
  },
  'save-ai-plans': {
    title: 'Saving AI-Generated Plans',
    category: 'AI Features',
    duration: '4 min',
    icon: Sparkles,
    description: 'How to save and customize AI-recommended plans',
    content: [
      {
        heading: 'Reviewing AI Suggestions',
        text: 'When you generate AI suggestions, you\'ll see multiple plan recommendations with pricing, structure, and revenue projections.'
      },
      {
        heading: 'Selecting a Plan',
        text: 'Click on any AI-suggested plan to see full details. Review the included services, pricing rationale, and projected performance.'
      },
      {
        heading: 'Saving the Plan',
        text: 'Click "Save This Plan" to add it to your membership plans. The plan is created in Draft status for you to review.'
      },
      {
        heading: 'Customizing',
        text: 'After saving, you can edit any aspect of the plan: name, price, services, or duration. Make it perfect for your clinic.'
      },
      {
        heading: 'Activating',
        text: 'When satisfied, toggle the plan to "Active" to make it available for member enrollment.'
      }
    ],
    tips: [
      'Save multiple AI suggestions to compare',
      'Customize plans to match your specific services',
      'Test pricing with a small member group first'
    ]
  },
  'upload-photos': {
    title: 'Upload Before & After Photos',
    category: 'Aesthetic Features',
    duration: '5 min',
    icon: Camera,
    description: 'How to upload and manage clinical photos',
    content: [
      {
        heading: 'Accessing the Gallery',
        text: 'Navigate to Aesthetic > Gallery from your dashboard. This is where all clinical photos are managed.'
      },
      {
        heading: 'Creating a New Entry',
        text: 'Click "Add Photos" and select a patient. Choose the treatment type and date.'
      },
      {
        heading: 'Uploading Photos',
        text: 'Upload the "Before" photo first, then the "After" photo. Ensure consistent lighting, angles, and backgrounds.'
      },
      {
        heading: 'Adding Details',
        text: 'Add treatment description, timeframe between photos, and any relevant notes for context.'
      },
      {
        heading: 'Privacy Settings',
        text: 'Choose visibility: "Private" (staff only) or "Public" (can be displayed on website). Always obtain patient consent first.'
      }
    ],
    tips: [
      'Use consistent photography standards',
      'Include treatment timeframes for context',
      'Always obtain written consent before publishing'
    ]
  }
};

const HelpArticle = () => {
  const { slug } = useParams();
  const article = helpArticles[slug];

  if (!article) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Card className="max-w-md">
          <CardContent className="pt-6 text-center">
            <BookOpen className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Article Not Found</h2>
            <p className="text-gray-600 mb-4">The help article you're looking for doesn't exist or has been moved.</p>
            <Button asChild>
              <Link to="/help">
                <ChevronLeft className="h-4 w-4 mr-2" />
                Back to Help Center
              </Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const Icon = article.icon;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Navigation */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/help" className="flex items-center gap-2 text-teal-600 hover:text-teal-700">
              <ChevronLeft className="h-5 w-5" />
              <span className="font-medium">Help Center</span>
            </Link>
            <Badge variant="outline">{article.category}</Badge>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-4 mb-4">
            <div className="h-14 w-14 rounded-xl bg-teal-100 flex items-center justify-center">
              <Icon className="h-7 w-7 text-teal-600" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">{article.title}</h1>
              <div className="flex items-center gap-3 mt-1">
                <span className="text-gray-500 flex items-center gap-1">
                  <Clock className="h-4 w-4" />
                  {article.duration} read
                </span>
              </div>
            </div>
          </div>
          <p className="text-lg text-gray-600">{article.description}</p>
        </div>

        {/* Content */}
        <Card>
          <CardContent className="py-6 space-y-6">
            {article.content.map((section, index) => (
              <div key={index}>
                <h2 className="text-xl font-semibold text-gray-900 mb-3">{section.heading}</h2>
                <p className="text-gray-700 leading-relaxed">{section.text}</p>
              </div>
            ))}

            {/* Checklist if available */}
            {article.checklist && (
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <CheckCircle2 className="h-5 w-5 text-teal-600" />
                  Checklist
                </h3>
                <ul className="space-y-2">
                  {article.checklist.map((item, idx) => (
                    <li key={idx} className="flex items-start gap-3">
                      <div className="h-5 w-5 rounded border-2 border-gray-300 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Tips if available */}
            {article.tips && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <h3 className="font-semibold text-amber-800 mb-3 flex items-center gap-2">
                  <Lightbulb className="h-5 w-5" />
                  Pro Tips
                </h3>
                <ul className="space-y-2">
                  {article.tips.map((tip, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-amber-900">
                      <span className="text-amber-600">•</span>
                      <span>{tip}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Navigation */}
        <div className="mt-8 flex justify-between">
          <Button variant="outline" asChild>
            <Link to="/help">
              <ChevronLeft className="h-4 w-4 mr-2" />
              Back to Help Center
            </Link>
          </Button>
          <Button 
            className="bg-teal-600 hover:bg-teal-700"
            onClick={() => window.location.href = 'mailto:support@mymedmember.com?subject=Help%20Article%20Question:%20' + encodeURIComponent(article.title)}
          >
            Still need help? Contact Support
          </Button>
        </div>
      </div>
    </div>
  );
};

export default HelpArticle;
